import { Icon } from "@iconify/react";
import { LoginBox } from "../components/loginBox";
import { Header1 } from "../components/headers";
import userBrowserLanguage from "../components/userBrowserLanguage";
import getTranslationJson from "../components/getTranslationJson";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { components as sso } from "../hooks/sso";
import axios, { AxiosError, type AxiosResponse } from "axios";
import userTimeZoneCountryCode from "../components/userTimeZoneCountryCode";
import { useState } from "react";
import { useParams } from "react-router";
import React from "react";
import { notificationGreenClass, notificationRedClass } from "../components/style";

function AuthConfirmation() {
  const { token } = useParams();
  const translation = getTranslationJson(userBrowserLanguage());
  const countryCode = userTimeZoneCountryCode();

  const queryClient = useQueryClient();

  const [successfullNotification, setSuccessfullNotification] = useState(0);
  const [failedRedirectNotification, setFailedRedirectNotification] = useState(false);
  const [errorNotification, setErrorNotification] = useState("");
  const [email, setEmail] = useState("");

  // Mutations
  const mutationVerifyEmail = useMutation({
    mutationKey: ["verify_email"],
    mutationFn: async (): Promise<AxiosResponse<sso["schemas"]["VerifyEmailResponse"]>> => {
      return axios.get(import.meta.env.VITE_PUBLIC_PLATFORM_SSO_API_URL! + "/verify-email/" + token, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    onSuccess: (data: AxiosResponse<sso["schemas"]["VerifyEmailResponse"]>) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["verify_email"] });

      // adding event listeners to detect if the redirect was successfully or not
      if (data.data.app_id === "GUMSmart" || data.data.app_id === "GUMPlaybrush") {
        let appOpened = false;
        const onHidden = () => {
          appOpened = true;
        };
        document.addEventListener("visibilitychange", onHidden);
        window.addEventListener("pagehide", onHidden);

        setTimeout(() => {
          document.removeEventListener("visibilitychange", onHidden);
          window.removeEventListener("pagehide", onHidden);

          if (!appOpened && document.visibilityState === "visible") {
            // show "app not installed" message or fallback URL
            setFailedRedirectNotification(true);
          }
        }, 1500);
      }
      // Redirect to the app
      if (data.data.app_id === "GUMPlaybrush") {
        window.location.href = `${import.meta.env.VITE_PUBLIC_UNITY_DEEPLINK_PREFIX_GUMPLAYBRUSH}://target?login${JSON.stringify(data.data.token)}`;
      }
      if (data.data.app_id === "GUMSmart") {
        window.location.href = `${import.meta.env.VITE_PUBLIC_UNITY_DEEPLINK_PREFIX_GUMSMART}://target?login${JSON.stringify(data.data.token)}`;
      }
      // Show success notification
      setSuccessfullNotification(data.status);
      setEmail(data.data.email);

      if (data.data.redirect_url) {
        setTimeout(() => {
          window.location.href = data.data.redirect_url!;
        }, 2000);
      }
    },
    onError: (data: AxiosError) => {
      // Invalidate and refetch
      console.error("Login error:", data);
      if (data.status === 400) {
        setErrorNotification(
          translation.verification_token_expired ||
            "Verification token expired. Please trigger the email verification process again.",
        );
      } else {
        setErrorNotification(data.message || "An error occurred while logging in.");
      }
    },
  });
  React.useEffect(() => {
    verifyEmailHandleSubmit();
  }, []);

  const verifyEmailHandleSubmit = () => {
    if (import.meta.env.VITE_PUBLIC_DEV === "true") {
      console.log("Time Zone Country Code:", countryCode);
      console.log("Browser Language:", userBrowserLanguage());
    }
    mutationVerifyEmail.mutate();
  };

  return (
    <div className="font-inter-24">
      <LoginBox>
        <header className="fflex flex-col mb-6 items-center justify-center">
          <Header1>{translation.email_verification || "Email verification"}</Header1>
        </header>
        {mutationVerifyEmail.isError && (
          <>
            <div className="flex items-center justify-center">
              <Icon
                icon="prime:times"
                className={`border border-red-200 bg-red-50 text-7xl text-red-700  rounded-[40px] mb-2 p-3`}
              />
            </div>
            <div className={notificationRedClass}>
              {errorNotification || "An error occurred while processing your request."}
            </div>
          </>
        )}
        {mutationVerifyEmail.isSuccess && (
          <>
            <div className="flex items-center justify-center">
              <Icon
                icon="prime:check"
                className={`border border-emerald-200 bg-emerald-50 text-7xl text-brand-green rounded-[40px] mb-2 p-3`}
              />
            </div>
            <div className={notificationGreenClass}>
              {successfullNotification == 202
                ? translation.email_verification_already_successfull || "Email verification already successful!"
                : translation.email_verification_successfull || "Email verification successful!"}
              <div>{email}</div>
            </div>
            <div className="text-sm text-gray-600 mt-4 mb-4">
              {translation.email_verification_successfull_description ||
                "You can now close this window and return to the login screen."}
            </div>
          </>
        )}
        {failedRedirectNotification && (
          <div className={notificationRedClass}>
            {translation.error_redirect_to_app ||
              "Failed to redirect to the app. Please make sure you have the latest version of the app installed."}
          </div>
        )}
        {mutationVerifyEmail.isPending && (
          <div className="flex items-center justify-center">
            <Icon icon="mdi:loading" className="animate-spin text-6xl text-brand-green" />
          </div>
        )}
      </LoginBox>
    </div>
  );
}
export default AuthConfirmation;
